import {
  ApiError,
  DailySheetController_export_v1,
  DailySheetController_page_v1,
  type DailySheetResource,
  messageForSheetBeforeOpeningBalance,
  type OpeningBalanceResource,
  OpeningBalancesController_current_v1,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import { Label } from '@repo/ui/components/label';
import { IconCircleCheck, IconDownload, IconLoader2 } from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { isForbidden, isNotFound, messageForFailure } from '@/api/failure';
import { useSession } from '@/auth/session';
import { systemClock } from '@/lib/clock';
import { formatPaise } from '@/lib/money';

import { DayPicker } from '../back-office/DayPicker';
import { Pager } from '../back-office/Pager';
import { type PageState } from '../back-office/pagination';
import { EmptyState, LoadFailed, Screen, ScreenFact } from '../back-office/Screen';
import { paths } from '../paths';
import { saveDownloadedFile } from '../reporting/cash-book-export';
import { defaultPeriod, periodProblem, type ReportingPeriod } from '../reporting/reporting-period';
import { formatBusinessDate, fromCalendarDate, toCalendarDate } from '../tickets/business-date';
import { ParkExpenseDialog, type ParkExpenseEdit } from './ParkExpenseDialog';
import { SheetDay } from './SheetDay';

/**
 * The park's daily sheet (#276): `GET /v1/daily-sheets`, owner and manager —
 * one page of the park's book per day, the outlets down it in the owner's
 * blocks, opening with one park-wide balance.
 *
 * It replaces a per-outlet sheet that was the transpose of the book. An
 * outlet's run of days is a filter of this read, not a second report.
 *
 * **The opening balance is the anchor.** No page exists before it, so the
 * sheet is not read until it is known, and the period is moved up to it.
 * Recording or correcting one is the owner's, on its own screen.
 *
 * **Paged by day**, a week at a time: each day is a page of twenty-odd lines,
 * and the period grows with what is asked for rather than with the park.
 */

type OpeningState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'none' }
  | { readonly kind: 'found'; readonly balance: OpeningBalanceResource }
  | { readonly kind: 'failed'; readonly error: unknown };

type SheetState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly days: readonly DailySheetResource[] }
  | { readonly kind: 'failed'; readonly error: unknown };

type ExportState =
  | { readonly kind: 'idle' | 'running' }
  | { readonly kind: 'failed'; error: unknown };

/** Saved as this only where the server's `Content-Disposition` did not arrive. */
const DAILY_SHEET_DEFAULT_FILENAME = 'daily-sheet.xlsx';

/** A week of pages at a time. */
const DAYS_PER_PAGE = 7;

const BANK_DEPOSITS_NOTE =
  'Bank deposits are not recorded yet, so the closing balance keeps growing.';

const linkClass =
  'rounded-3xl text-foreground underline underline-offset-4 outline-none focus-visible:ring-3 focus-visible:ring-ring/30';

/** A period moved up so it starts no earlier than the sheet does. */
const startingNoEarlierThan = (period: ReportingPeriod, day: string): ReportingPeriod => ({
  from: period.from < day ? day : period.from,
  to: period.to < day ? day : period.to,
});

export function DailySheet() {
  const client = useApiClient();
  const { session } = useSession();

  // Courtesy, not security: recording is `@RequireRole('OWNER')` on the API.
  const isOwner = session?.user.role === 'OWNER';

  const [opening, setOpening] = useState<OpeningState>({ kind: 'loading' });

  const [period, setPeriod] = useState<ReportingPeriod>(() => defaultPeriod(systemClock));
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<PageState>({ offset: 0, limit: DAYS_PER_PAGE, total: 0 });
  const [sheet, setSheet] = useState<SheetState>({ kind: 'loading' });

  const [editing, setEditing] = useState<ParkExpenseEdit | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [exporting, setExporting] = useState<ExportState>({ kind: 'idle' });

  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((count) => count + 1), []);
  const [sheetReloads, setSheetReloads] = useState(0);
  const reloadSheet = useCallback(() => setSheetReloads((count) => count + 1), []);

  const problem = periodProblem(period, systemClock);
  const complete = period.from !== '' && period.to !== '' && problem === null;

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    setOpening({ kind: 'loading' });

    client
      .request(OpeningBalancesController_current_v1, { signal: controller.signal })
      .then((balance) => {
        if (!live) return;
        setOpening({ kind: 'found', balance });
        // Nothing before it can be answered, so asking would only be refused.
        setPeriod((held) => startingNoEarlierThan(held, balance.businessDate));
      })
      .catch((error: unknown) => {
        if (!live || (error instanceof DOMException && error.name === 'AbortError')) return;
        // A 404 here is the answer "none recorded", not a failure to answer.
        setOpening(isNotFound(error) ? { kind: 'none' } : { kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, reloads]);

  // Keyed on the balance's id, not the object: a correction is a new id, and
  // every page worked forward from it has to be read again.
  const anchorId = opening.kind === 'found' ? opening.balance.id : null;

  useEffect(() => {
    if (anchorId === null || !complete) return;

    const controller = new AbortController();
    let live = true;

    setSheet({ kind: 'loading' });

    client
      .request(DailySheetController_page_v1, {
        query: { from: period.from, to: period.to, offset, limit: DAYS_PER_PAGE },
        signal: controller.signal,
      })
      .then((answer) => {
        if (!live) return;
        setSheet({ kind: 'loaded', days: answer.items });
        setPage({ offset: answer.offset, limit: answer.limit, total: answer.total });
      })
      .catch((error: unknown) => {
        if (live && !(error instanceof DOMException && error.name === 'AbortError')) {
          setSheet({ kind: 'failed', error });
        }
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, anchorId, period.from, period.to, complete, offset, sheetReloads]);

  /** A filter change is a new first page. */
  const changeFilter = (apply: () => void) => {
    apply();
    setOffset(0);
  };

  /**
   * The workbook for the period on screen, on the authenticated transport. The
   * route reads `Authorization: Bearer` and nothing else, so an anchor pointed
   * at it would save the refusal envelope as a `.xlsx` — see
   * `reporting/cash-book-export.ts`.
   */
  const runExport = () => {
    if (anchorId === null || !complete || exporting.kind === 'running') return;

    setExporting({ kind: 'running' });

    client
      .download(DailySheetController_export_v1, {
        query: { from: period.from, to: period.to },
      })
      .then((file) => {
        saveDownloadedFile(file, DAILY_SHEET_DEFAULT_FILENAME);
        setExporting({ kind: 'idle' });
      })
      .catch((error: unknown) => {
        setExporting({ kind: 'failed', error });
      });
  };

  const recordLink = isOwner ? (
    <Button nativeButton={false} render={<Link to={paths.dailySheetOpeningBalance} />}>
      Record the opening balance
    </Button>
  ) : undefined;

  /** Where the sheet cannot start: the day it does, or that it has none yet. */
  const startNotice = (startsOn: string | null): ReactNode => (
    <Notice>
      <EmptyState
        title={messageForSheetBeforeOpeningBalance(
          startsOn === null ? null : formatBusinessDate(startsOn),
        )}
        description={
          startsOn !== null
            ? 'There is no sheet before it.'
            : isOwner
              ? 'Record it to start the sheet.'
              : 'The owner records it.'
        }
        action={
          startsOn !== null ? (
            <Button
              variant="outline"
              onClick={() => changeFilter(() => setPeriod((held) => startingFrom(held, startsOn)))}
            >
              Start from {formatBusinessDate(startsOn)}
            </Button>
          ) : (
            recordLink
          )
        }
      />
    </Notice>
  );

  const failed = (error: unknown, onRetry: () => void): ReactNode => (
    <Notice>
      {isForbidden(error) ? (
        <EmptyState
          title="This account cannot read the daily sheet"
          description="If you are an owner or manager, sign out and back in."
        />
      ) : (
        <LoadFailed error={error} onRetry={onRetry} title="The daily sheet could not be loaded" />
      )}
    </Notice>
  );

  let body: ReactNode;

  if (opening.kind === 'loading' || (opening.kind === 'found' && sheet.kind === 'loading')) {
    body = <SheetSkeleton />;
  } else if (opening.kind === 'failed') {
    body = failed(opening.error, reload);
  } else if (opening.kind === 'none') {
    body = startNotice(null);
  } else if (sheet.kind === 'failed') {
    const startsOn = sheetStartsOn(sheet.error);

    body = startsOn === undefined ? failed(sheet.error, reloadSheet) : startNotice(startsOn);
  } else if (sheet.kind === 'loaded') {
    body = (
      <>
        {sheet.days.map((day) => (
          <SheetDay
            key={day.businessDate}
            day={day}
            onAddExpense={(businessDate) => setEditing({ kind: 'new', businessDate })}
            onCorrectExpense={(line) => setEditing({ kind: 'correct', line })}
          />
        ))}
        <Pager state={page} label="Daily sheet" onOffsetChange={setOffset} />
      </>
    );
  }

  return (
    <Screen
      title="Daily sheet"
      lede="One page of the park’s book per day"
      action={
        <Button
          onClick={runExport}
          disabled={anchorId === null || !complete || exporting.kind === 'running'}
        >
          {exporting.kind === 'running' ? (
            <IconLoader2 data-icon="inline-start" className="animate-spin" />
          ) : (
            <IconDownload data-icon="inline-start" />
          )}
          {exporting.kind === 'running' ? 'Exporting…' : 'Export to Excel'}
        </Button>
      }
      aside={
        <>
          <PeriodPicker
            id="sheet-from"
            label="From"
            value={period.from}
            onChange={(from) => changeFilter(() => setPeriod((held) => ({ ...held, from })))}
          />
          <PeriodPicker
            id="sheet-to"
            label="To"
            value={period.to}
            onChange={(to) => changeFilter(() => setPeriod((held) => ({ ...held, to })))}
          />

          {problem !== null && (
            <p role="status" className="text-sm text-destructive">
              {problem}
            </p>
          )}

          <ScreenFact label="Opening balance">
            {opening.kind === 'found' ? (
              <span className="numeric">
                {`${formatPaise(opening.balance.amountPaise)} at the start of ${formatBusinessDate(opening.balance.businessDate)}`}
              </span>
            ) : opening.kind === 'none' ? (
              'None recorded yet'
            ) : (
              '—'
            )}
            {isOwner && opening.kind === 'found' && (
              <>
                {' '}
                <Link to={paths.dailySheetOpeningBalance} className={linkClass}>
                  Correct it
                </Link>
              </>
            )}
          </ScreenFact>

          <ScreenFact label="Closing balance">{BANK_DEPOSITS_NOTE}</ScreenFact>

          <ScreenFact label="Layout">
            <Link to={paths.dailySheetLayout} className={linkClass}>
              {isOwner ? 'Arrange the blocks' : 'See the blocks'}
            </Link>
          </ScreenFact>

          {exporting.kind === 'failed' && (
            <p role="status" className="text-sm text-destructive">
              The export could not be made. {messageForFailure(exporting.error)}
            </p>
          )}
        </>
      }
    >
      {notice !== null && (
        <Alert role="status">
          <IconCircleCheck />
          <AlertDescription>{notice}</AlertDescription>
        </Alert>
      )}

      {body}

      {editing !== null && (
        <ParkExpenseDialog
          edit={editing}
          onClose={() => setEditing(null)}
          onStale={reloadSheet}
          onRecorded={(line) => {
            setEditing(null);
            setNotice(
              line.amountPaise === 0
                ? `Removed from ${formatBusinessDate(line.businessDate)}: ${line.reason}.`
                : `Recorded on ${formatBusinessDate(line.businessDate)}: ${line.reason}, ${formatPaise(line.amountPaise)}.`,
            );
            reloadSheet();
          }}
        />
      )}
    </Screen>
  );
}

/** A period starting exactly on `day` — the one the refusal said it starts on. */
const startingFrom = (period: ReportingPeriod, day: string): ReportingPeriod => ({
  from: day,
  to: period.to < day ? day : period.to,
});

/**
 * The day the refusal says the sheet starts on — `null` when the park has no
 * opening balance — or `undefined` when this is some other failure.
 */
function sheetStartsOn(error: unknown): string | null | undefined {
  if (
    !(error instanceof ApiError) ||
    error.code !== 'REPORTING_DAILY_SHEET_BEFORE_OPENING_BALANCE'
  ) {
    return undefined;
  }

  const day = error.details?.['openingBalanceDate'];

  return typeof day === 'string' ? day : null;
}

/** Centred in the records band, as `TableNotice` centres on a list screen. */
function Notice({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-1 items-center justify-center px-6 py-14">
      <div className="max-w-md space-y-3 text-center">{children}</div>
    </div>
  );
}

/** A page's shape while it is in flight: a date and a ruled block of lines. */
function SheetSkeleton() {
  return (
    <div aria-hidden className="space-y-3">
      <div className="h-5 w-32 animate-pulse rounded-3xl bg-muted" />
      {Array.from({ length: 8 }, (_, row) => (
        <div
          key={row}
          className="h-4 animate-pulse rounded-3xl bg-muted"
          style={{ width: `${String(45 + ((row * 7) % 45))}%` }}
        />
      ))}
    </div>
  );
}

function PeriodPicker({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (day: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm text-muted-foreground">
        {label}
      </Label>
      <DayPicker
        id={id}
        value={value}
        emptyLabel="Choose a day"
        format={formatBusinessDate}
        fromDate={fromCalendarDate}
        toDate={toCalendarDate}
        onValueChange={onChange}
      />
    </div>
  );
}
